import type { RecurringGroup, RecurringOverviewResponse, RecurringPayment } from '@/lib/api';

export interface RecurringReviewQueue {
  detected: RecurringPayment[];
  failedCancels: RecurringPayment[];
}

function displayName(item: RecurringPayment): string {
  return (item.merchantName ?? item.categoryTranslated ?? item.category ?? item.normalizedKey ?? 'Recorrente')
    .toLowerCase();
}

// Next charge first, undated last, then by name — the order the API gives its lists.
function byNextCharge(a: RecurringPayment, b: RecurringPayment): number {
  if (a.nextExpectedDate !== b.nextExpectedDate) {
    if (!a.nextExpectedDate) return 1;
    if (!b.nextExpectedDate) return -1;
    return a.nextExpectedDate < b.nextExpectedDate ? -1 : 1;
  }
  const left = displayName(a);
  const right = displayName(b);
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

/** "Detectados" and "Voltaram a cobrar": the overview's live series in those statuses. */
export function reviewQueue(groups: RecurringGroup[] | undefined): RecurringReviewQueue {
  const items = groups?.flatMap((group) => group.items) ?? [];
  return {
    detected: items.filter((item) => item.status === 'Detected').sort(byNextCharge),
    failedCancels: items.filter((item) => item.status === 'FailedCancellation').sort(byNextCharge),
  };
}

/**
 * One series as an action leaves it, patched in place. A series the action ends stays in its group
 * until the refetch that follows every action drops it, so the lists never disagree with the
 * totals the same response carries.
 */
export function patchOverviewItem(
  overview: RecurringOverviewResponse,
  id: string,
  patch: Partial<RecurringPayment>,
): RecurringOverviewResponse {
  return {
    ...overview,
    groups: overview.groups.map((group) => ({
      ...group,
      items: group.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    })),
  };
}
